import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { StoryblokCache, initializeRedis } from '@cache/redis';
import { OllieStoryblokClient } from '@web/cms/api';
import { PrefixedReporterProvider } from '../../reporter';
import { server, shared } from '../../config';
import { CMSMeta } from '../../cms';
import { withFlagshipSession } from '../../auth/withFlagshipSession';
import { withGladlySetting } from '../../gladly';
import { Redirect } from '../../router';
import { ONBOARDING_FLOW_UI, useExperiment } from '../../experiments';
import {
  RECIPES_SELECTION_SUBPATH,
  QUIZ_SUBPATH,
  VALID_SUBPATHS,
  VALID_SLUGS,
  OnboardingFlowContainer,
  ADD_PUP_SUBPATH,
  EDIT_PET_SUBPATH,
  DELETE_PET_SUBPATH,
} from '../../onboarding';

const OnboardingPage = ({ ...props }) => {
  const { value: OnboardingUI } = useExperiment(ONBOARDING_FLOW_UI, 'control');
  if (OnboardingUI.includes('cro_optimized')) {
    if (
      props.subpath === 'addPet' ||
      props.subpath === 'editPet' ||
      props.subpath === 'deletePet'
    ) {
      return (
        <Redirect
          to={`/onboarding-v2/${
            props.subpath
              ? props.subpath + (props.slug ? `/${props.slug}` : '')
              : ''
          }${
            props.query && props.query.pet_id
              ? `?pet_id=${props.query.pet_id}`
              : ''
          }`}
        />
      );
    } else {
      return (
        <Redirect
          to={`/onboarding-v2/${
            props.query && props.query.pet_id
              ? `?pet_id=${props.query.pet_id}`
              : ''
          }`}
        />
      );
    }
  } else {
    return (
      <>
        <Head>
          <script
            type="text/javascript"
            src={`${shared.AWS_WAF_CHALLENGE_INTEGRATION_URL}`}
            defer
          />
        </Head>
        <PrefixedReporterProvider prefix="Onboarding">
          <CMSMeta content={props.content} />
          <OnboardingFlowContainer {...props} />
        </PrefixedReporterProvider>
      </>
    );
  }
};

/**
 * @type {import('next').GetServerSideProps}
 */
export const getServerSideProps = withGladlySetting(
  withFlagshipSession(async ({ params, query }) => {
    const [subpath, slug, ...rest] = params.path ?? [];

    // If anything comes after the first two params,
    // e.g. /onboarding/quiz/something/else/
    // that's invalid, 404.
    if (rest.length > 0) {
      return {
        notFound: true,
      };
    }

    // 404 if the subpath exists & isn't in the list.
    // !subpath is valid; `/onboarding/` will redirect appropriately.
    if (subpath && !VALID_SUBPATHS.includes(subpath)) {
      return {
        notFound: true,
      };
    }

    const stateMachineSubpath =
      ADD_PUP_SUBPATH === subpath ||
      subpath === EDIT_PET_SUBPATH ||
      subpath === DELETE_PET_SUBPATH;
    const isSubpathSlugless =
      subpath !== QUIZ_SUBPATH && subpath !== RECIPES_SELECTION_SUBPATH;

    // If we have a slug, validate it.
    if (slug) {
      // Only quiz and recipes have extra slugs.
      if (isSubpathSlugless) {
        return {
          notFound: true,
        };
      }

      // Check if valid question slug.
      if (!VALID_SLUGS.includes(slug)) {
        return {
          notFound: true,
        };
      }
    }

    let content = {
      component: 'redirect_route',
    };

    // Narrows down to the pages that exist in the CMS
    if (subpath && (isSubpathSlugless || slug) && !stateMachineSubpath) {
      const redis = initializeRedis({
        host: server.REDIS_HOST,
        port: server.REDIS_PORT,
      });
      const storyblokRedis = new StoryblokCache(redis);
      const ollieStoryblokClient = new OllieStoryblokClient(storyblokRedis);
      const storySlug = `/onboarding/${subpath + (slug ? `/${slug}` : '')}`;
      content = (await ollieStoryblokClient.getStory(storySlug)).data.story
        .content;
    }

    return {
      props: {
        content,
        subpath: subpath || null,
        slug: slug || null,
        query: query || {},
      },
    };
  }),
);

OnboardingPage.displayName = 'OnboardingPage';

OnboardingPage.propTypes = {
  content: PropTypes.object.isRequired,
};

export default OnboardingPage;
